import { create } from 'zustand';
import { audioManager } from '../game/audio/audioManager';
import { bandForLevel, isTutorialLevel } from '../game/generation/director';
import { getTuning } from '../game/generation/config/tuning';
import { haptics } from '../game/haptics/haptics';
import { LevelService } from '../game/levelService';
import {
  createLevelIdentity,
  DEFAULT_CAMPAIGN_ID,
  type LevelIdentity,
} from '../game/model/identity';
import type { GeneratedLevel } from '../game/model/level';
import { DIFFICULTY_BAND_LABELS } from '../game/model/recipe';
import {
  Repository,
  readSettingsSync,
  type RepositoryStatus,
} from '../game/persistence/repository';
import {
  createEmptyProgress,
  type CampaignProgress,
  type Settings,
} from '../game/persistence/schema';
import { GameSession, type MoveResult, type SessionSnapshot } from '../game/rules/gameSession';
import { ActiveTimer } from './activeTimer';

/**
 * Application store.
 *
 * Holds three clearly separated things: persistent application state (settings, progress),
 * the current logical game state (a `GameSession` plus a plain snapshot for rendering), and
 * UI phase. Per-frame animation state lives in the PixiJS layer and never enters this store,
 * so React does not re-render while arrows move.
 */

export type AppPhase = 'booting' | 'ready' | 'error';
export type LevelPhase = 'idle' | 'loading' | 'ready' | 'clearing' | 'won' | 'lost' | 'error';

export interface LevelSummary {
  readonly levelNumber: number;
  readonly difficultyLabel: string;
  readonly isTutorial: boolean;
}

interface AppStateValues {
  phase: AppPhase;
  bootError: string | null;
  storage: RepositoryStatus;
  settings: Settings;
  progress: CampaignProgress;

  levelPhase: LevelPhase;
  levelError: string | null;
  level: GeneratedLevel | null;
  session: GameSession | null;
  snapshot: SessionSnapshot | null;
  summary: LevelSummary | null;
  hintArrowId: number;
  focusArrowId: number;
  paused: boolean;
  elapsedMs: number;
  resumedFromSave: boolean;
  /**
   * Bumped whenever the renderer must re-apply the board from the session: starting a level,
   * resuming a saved one, or restarting. Restart reuses the same immutable board object, so
   * object identity alone cannot tell the renderer that every arrow is back.
   */
  boardEpoch: number;
}

interface AppStateActions {
  boot(): Promise<void>;
  updateSettings(partial: Partial<Settings>): Promise<void>;
  startLevel(levelNumber: number, options?: { resume?: boolean }): Promise<void>;
  tapArrow(arrowId: number): MoveResult;
  settleWin(): void;
  requestHint(): number;
  setFocusArrow(arrowId: number): void;
  restartLevel(): void;
  setPaused(paused: boolean): void;
  leaveLevel(): void;
  advanceToNextLevel(): Promise<void>;
  resetProgress(): Promise<void>;
  persistInterrupted(): void;
}

export type AppStore = AppStateValues & AppStateActions;

const repository = new Repository();
const levelService = new LevelService(repository);
const timer = new ActiveTimer();

/** Incremented for each level start; results from stale loads are discarded. */
let loadToken = 0;

function summarise(levelNumber: number, contentVersion: number): LevelSummary {
  const band = bandForLevel(levelNumber, getTuning(contentVersion));
  return {
    levelNumber,
    difficultyLabel: DIFFICULTY_BAND_LABELS[band],
    isTutorial: isTutorialLevel(levelNumber),
  };
}

function identityFor(levelNumber: number): LevelIdentity {
  return createLevelIdentity(levelNumber);
}

export const useAppStore = create<AppStore>()((set, get) => ({
  phase: 'booting',
  bootError: null,
  storage: { storageAvailable: false },
  settings: readSettingsSync(),
  progress: createEmptyProgress(DEFAULT_CAMPAIGN_ID),

  levelPhase: 'idle',
  levelError: null,
  level: null,
  session: null,
  snapshot: null,
  summary: null,
  hintArrowId: -1,
  focusArrowId: -1,
  paused: false,
  elapsedMs: 0,
  resumedFromSave: false,
  boardEpoch: 0,

  async boot() {
    try {
      const storage = await repository.open();
      const [settings, progress] = await Promise.all([
        repository.loadSettings(),
        repository.loadProgress(DEFAULT_CAMPAIGN_ID),
      ]);
      audioManager.configure(settings);
      haptics.setEnabled(settings.hapticsEnabled);
      set({ phase: 'ready', storage, settings, progress });
    } catch (error) {
      // Booting must not be able to leave the player stuck on a blank screen: fall back to
      // defaults and let them play without persistence.
      set({
        phase: 'ready',
        bootError: error instanceof Error ? error.message : String(error),
        storage: { storageAvailable: false, degradedReason: 'boot failed' },
      });
    }
  },

  async updateSettings(partial) {
    const settings = { ...get().settings, ...partial };
    set({ settings });
    audioManager.configure(settings);
    haptics.setEnabled(settings.hapticsEnabled);
    await repository.saveSettings(settings);
  },

  async startLevel(levelNumber, options = {}) {
    const token = ++loadToken;
    timer.reset();
    set({
      levelPhase: 'loading',
      levelError: null,
      level: null,
      session: null,
      snapshot: null,
      hintArrowId: -1,
      focusArrowId: -1,
      paused: false,
      elapsedMs: 0,
      resumedFromSave: false,
      summary: summarise(levelNumber, identityFor(levelNumber).contentVersion),
    });

    try {
      const identity = identityFor(levelNumber);
      const level = await levelService.getLevel(identity);
      if (token !== loadToken) return;

      let session: GameSession | null = null;
      let resumed = false;
      let elapsed = 0;

      if (options.resume === true) {
        const saved = await repository.loadInterrupted(DEFAULT_CAMPAIGN_ID);
        if (
          saved !== null &&
          saved.identity.levelNumber === levelNumber &&
          saved.boardHash === level.boardHash
        ) {
          const restored = GameSession.restore(level, saved);
          if (restored !== null && restored.status === 'ready') {
            session = restored;
            resumed = true;
            elapsed = saved.elapsedMs;
          }
        }
      }
      if (session === null) session = new GameSession(level);
      if (token !== loadToken) return;

      timer.reset(elapsed);
      timer.start();
      // Warm the next level while the player reads the board.
      if (levelNumber + 1 <= 100000) levelService.prefetch(identityFor(levelNumber + 1));

      set({
        levelPhase: 'ready',
        level,
        session,
        snapshot: session.snapshot(),
        elapsedMs: elapsed,
        resumedFromSave: resumed,
        boardEpoch: get().boardEpoch + 1,
      });
    } catch (error) {
      if (token !== loadToken) return;
      set({
        levelPhase: 'error',
        levelError: error instanceof Error ? error.message : String(error),
      });
    }
  },

  tapArrow(arrowId) {
    const { session, levelPhase, paused } = get();
    if (session === null || levelPhase !== 'ready' || paused) {
      return { kind: 'ignored', reason: 'not-ready' };
    }

    const result = session.tap(arrowId);
    if (result.kind === 'ignored') return result;

    const snapshot = session.snapshot();
    const updates: Partial<AppStateValues> = { snapshot, hintArrowId: -1 };

    if (result.kind === 'removed') {
      audioManager.play('remove');
      haptics.vibrate('remove');
      if (result.won) {
        // Freeze the clock at the winning tap, but hold the win itself — dialog, fanfare and
        // persistence — until the last arrow has finished sliding off the board. The scene
        // calls settleWin() when that exit animation completes.
        timer.pause();
        updates.levelPhase = 'clearing';
        updates.elapsedMs = timer.elapsedMs;
      }
    } else {
      audioManager.play(result.lost ? 'level-failed' : 'blocked');
      haptics.vibrate(result.lost ? 'heart-lost' : 'blocked');
      if (result.lost) {
        timer.pause();
        updates.levelPhase = 'lost';
        updates.elapsedMs = timer.elapsedMs;
        void repository.clearInterrupted(DEFAULT_CAMPAIGN_ID);
      }
    }

    set(updates);
    return result;
  },

  settleWin() {
    // Fired by the board scene once the winning arrow's exit animation finishes. Guarded so a
    // late or duplicate call — or one arriving after a restart — cannot resurrect the dialog.
    if (get().levelPhase !== 'clearing') return;
    set({ levelPhase: 'won' });
    audioManager.play('level-complete');
    haptics.vibrate('complete');
    void finishLevel(get, set, timer.elapsedMs);
  },

  requestHint() {
    const { session, levelPhase } = get();
    if (session === null || levelPhase !== 'ready') return -1;
    const arrowId = session.requestHint();
    set({ hintArrowId: arrowId, snapshot: session.snapshot() });
    return arrowId;
  },

  setFocusArrow(arrowId) {
    set({ focusArrowId: arrowId });
  },

  restartLevel() {
    const { session } = get();
    if (session === null) return;
    session.restart();
    timer.reset();
    timer.start();
    set({
      session,
      snapshot: session.snapshot(),
      levelPhase: 'ready',
      hintArrowId: -1,
      focusArrowId: -1,
      paused: false,
      elapsedMs: 0,
      resumedFromSave: false,
      boardEpoch: get().boardEpoch + 1,
    });
  },

  setPaused(paused) {
    const { levelPhase } = get();
    if (levelPhase !== 'ready') return;
    if (paused) timer.pause();
    else timer.start();
    audioManager.setActive(!paused);
    set({ paused, elapsedMs: timer.elapsedMs });
  },

  leaveLevel() {
    get().persistInterrupted();
    loadToken += 1;
    timer.reset();
    set({
      levelPhase: 'idle',
      level: null,
      session: null,
      snapshot: null,
      summary: null,
      hintArrowId: -1,
      focusArrowId: -1,
      paused: false,
      elapsedMs: 0,
    });
  },

  async advanceToNextLevel() {
    const current = get().summary?.levelNumber ?? 1;
    await get().startLevel(current + 1);
  },

  async resetProgress() {
    await repository.resetProgress(DEFAULT_CAMPAIGN_ID);
    set({ progress: createEmptyProgress(DEFAULT_CAMPAIGN_ID) });
  },

  persistInterrupted() {
    const { session, levelPhase } = get();
    if (session === null || levelPhase !== 'ready') return;
    if (session.removedArrowIds.length === 0 && session.heartsRemaining === 3) return;
    void repository.saveInterrupted(
      DEFAULT_CAMPAIGN_ID,
      session.toInterruptedState(timer.elapsedMs),
    );
  },
}));

async function finishLevel(
  get: () => AppStore,
  set: (partial: Partial<AppStateValues>) => void,
  elapsedMs: number,
): Promise<void> {
  const { session, level } = get();
  if (session === null || level === null) return;

  const progress = await repository.recordCompletion(
    DEFAULT_CAMPAIGN_ID,
    {
      levelNumber: level.identity.levelNumber,
      boardHash: level.boardHash,
      bestTimeMs: Math.round(elapsedMs),
      minMistakes: session.mistakes,
      minHintsUsed: session.hintsUsed,
      attempts: 1,
      generatorVersion: level.identity.generatorVersion,
      stencilLibraryVersion: level.identity.stencilLibraryVersion,
      contentVersion: level.identity.contentVersion,
    },
    true,
  );
  await repository.clearInterrupted(DEFAULT_CAMPAIGN_ID);
  set({ progress });
}

/** Exposed for the developer lab and tests; the app itself goes through the store. */
export function getLevelService(): LevelService {
  return levelService;
}

export function getRepository(): Repository {
  return repository;
}

export function getActiveTimer(): ActiveTimer {
  return timer;
}
